import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
/**
 * @interface InputProps
 * @description Define las propiedades del componente Input.
 */
interface InputProps {
  /**
   * @property {string} label - El label del componente Input.
   */
  label?: string;
  /**
   * @property {string} type - El tipo del componente Input.
   */
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' | 'file';
  /**
   * @property {string} value - El valor del componente Input.
   */
  value?: string | number;
  /**
   * @property {function} onChange - La función de cambio del componente Input.
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * @property {boolean} required - Indica si el componente Input es requerido.
   */
  required?: boolean;
  /**
   * @property {string} error - El error del componente Input.
   */
  error?: string;
  /**
   * @property {string} helperText - El helper text del componente Input.
   */
  helperText?: string;
  /**
   * @property {React.ReactNode} leftIcon - El icono izquierdo del componente Input.
   */
  leftIcon?: React.ReactNode;
  /**
   * @property {React.ReactNode} rightIcon - El icono derecho del componente Input.
   */
  rightIcon?: React.ReactNode;
  /**
   * @property {string} className - La clase del componente Input.
   */
  className?: string;
  /**
   * @property {string} id - El id del componente Input.
   */
  id?: string;
  /**
   * @property {boolean} readOnly - Indica si el componente Input es de solo lectura.
   */
  readOnly?: boolean;
  /**
   * @property {string} placeholder - El placeholder del componente Input.
   */
  placeholder?: string;
  /**
   * @property {string} accept - Tipos de archivo aceptados (solo para type="file").
   */
  accept?: string;
  /**
   * @property {boolean} multiple - Permite selección múltiple de archivos (solo para type="file").
   */
  multiple?: boolean;
  /**
   * @property {boolean} disabled - Indica si el componente Input está deshabilitado.
   */
  disabled?: boolean;
  /**
   * @property {string} autoComplete - Valor del atributo `autocomplete` nativo (ej. "off", "new-password", "username").
   * Úsalo para evitar que el navegador autorrellene campos de formularios que crean cuentas de OTRO usuario
   * (no la sesión actual), donde el autocompletado del propio usuario/contraseña guardados sería incorrecto.
   */
  autoComplete?: string;
  /**
   * @property {string} ariaLabel - Nombre accesible del campo cuando no lleva `label` visible (por ejemplo,
   * un buscador con solo ícono y placeholder). El placeholder no sustituye a un nombre.
   */
  ariaLabel?: string;
}



/**
 * Si el label termina en `*` (marcador de campo obligatorio ya usado en los formularios),
 * lo separa y lo antepone en rojo al inicio del texto en vez de dejarlo al final en negro.
 * Labels sin `*` se devuelven tal cual.
 */
const renderLabelWithAsterisk = (label: string): React.ReactNode => {
  const trimmed = label.trimEnd();
  if (!trimmed.endsWith('*')) return label;
  const base = trimmed.slice(0, -1).trimEnd();
  return (
    <>
      <span className="text-red-600 font-bold">*</span> {base}
    </>
  );
};

/**
 * @component Input
 * @description Un componente de entrada personalizable con soporte para etiquetas, errores, texto de ayuda e iconos.
 * Soporta diferentes tipos de input incluyendo archivos (file).
 * @param {InputProps} props Las propiedades del componente.
 * @returns {React.ReactElement} El componente de entrada renderizado.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  type = 'text',
  value,
  onChange,
  required = false,
  leftIcon,
  rightIcon,
  className = '',
  id,
  readOnly = false,
  accept,
  multiple,
  disabled = false,
  ariaLabel,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const mensajeId = `${inputId}-mensaje`;
  const tieneMensaje = Boolean(error) || Boolean(helperText);
  const isFileType = type === 'file';
  const isPasswordType = type === 'password';
  const [showPassword, setShowPassword] = React.useState(false);
  const resolvedType = isPasswordType && showPassword ? 'text' : type;

  // Para inputs de tipo file, no se usa value (por seguridad del navegador)
  const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    id: inputId,
    type: resolvedType,
    onChange,
    required,
    readOnly,
    disabled,
    accept: isFileType ? accept : undefined,
    multiple: isFileType && multiple ? multiple : undefined,
    // El error o la ayuda se leen con el campo, y un error lo marca como inválido.
    'aria-invalid': error ? true : undefined,
    'aria-describedby': tieneMensaje ? mensajeId : undefined,
    ...props,
    // Solo si viene: así el campo con `label` visible no recibe un `aria-label` vacío que lo tape.
    ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
  };

  // Solo agregar value si no es tipo file
  if (!isFileType && value !== undefined) {
    inputProps.value = value;
  }

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {renderLabelWithAsterisk(label)}
        </label>
      )}
      <div className="relative">
        {leftIcon && !isFileType && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          {...inputProps}
          className={`
            block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 ${isPasswordType ? 'pr-10' : ''}
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${
              error
                ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                : 'border-gray-300'
            }
            ${leftIcon && !isFileType ? 'pl-10' : ''}
            ${rightIcon && !isFileType && !isPasswordType ? 'pr-10' : ''}
            ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}
            ${className}
          `}
        />
        {isPasswordType ? (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            disabled={disabled || readOnly}
            tabIndex={-1}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </button>
        ) : (
          rightIcon &&
          !isFileType && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )
        )}
      </div>
      {error && <p id={mensajeId} className="mt-1 text-sm text-error-600">{error}</p>}
      {helperText && !error && (
        <p id={mensajeId} className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
